import { useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate } from "react-router-dom";
import { IconButton, Box, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ModuleLayout from "./ModuleLayout/ModuleLayout";
import {
  tireValidationSchema,
  receiveStockValidationSchema,
} from "./FormModal/validationSchemas";
import ReusableFormDialog from "./FormModal/ReusableFormDialog";
import useVendorOptions from "../hooks/useVendorOptions";
import useLocationOptions from "../hooks/useLocationOptions";
import {
  buildTirePayload,
  createTireInitialValues,
  getTireFormFields,
  prepareTireFormValues,
} from "./Tires/tireFormConfig";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
} from "@mui/material";

function formatMoney(value) {
  return `$ ${Number(value || 0).toFixed(2)}`;
}

function MaskedBuyPriceCell({ value }) {
  const [showBuyPrice, setShowBuyPrice] = useState(false);
  const hasValue = value !== "" && value != null;

  if (!hasValue) return "-";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box component="span" sx={{ minWidth: 54 }}>
        {showBuyPrice ? formatMoney(value) : "$ ****"}
      </Box>
      <IconButton
        aria-label={showBuyPrice ? "Hide buy price" : "Show buy price"}
        size="small"
        title={showBuyPrice ? "Hide buy price" : "Show buy price"}
        onClick={(event) => {
          event.stopPropagation();
          setShowBuyPrice((prev) => !prev);
        }}
      >
        {showBuyPrice ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
}

function Tires() {
  const navigate = useNavigate();
  const tireInit = createTireInitialValues(Helpers.authParentId);

  const receiveStockInit = {
    quantity: "",
    vendor: "",
    invoiceNo: "",
    po: "",
    date: new Date().toISOString().slice(0, 10),
    note: "",
  };

  const [openReceiveDialog, setOpenReceiveDialog] = useState(false);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveForm, setReceiveForm] = useState(receiveStockInit);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const receiveSubmitLock = useRef(false);
  const [qualityFilter, setQualityFilter] = useState("All");
  const [excludeZeroQty, setExcludeZeroQty] = useState(false);
  const [totalNewQty, setTotalNewQty] = useState(0);
  const [totalUsedQty, setTotalUsedQty] = useState(0);
  const { getVendorFieldOptions } = useVendorOptions(navigate);
  const locationOptions = useLocationOptions(navigate);

  const tireFields = useCallback(
    (item = {}) => getTireFormFields(getVendorFieldOptions(item?.vendor), locationOptions),
    [getVendorFieldOptions, locationOptions],
  );

  // Receive stock form fields configuration
  const receiveStockFields = useMemo(
    () => [
      { name: "quantity", label: "Quantity", type: "number" },
      {
        name: "vendor",
        label: "Vendor",
        type: "select",
        options: getVendorFieldOptions(receiveForm?.vendor),
      },
      { name: "invoiceNo", label: "Invoice No.", type: "text" },
      { name: "po", label: "PO", type: "text" },
      { name: "date", label: "Date", type: "date" },
      { name: "note", label: "Note", type: "text", multiline: true, rows: 2 },
    ],
    [getVendorFieldOptions, receiveForm?.vendor],
  );

  // Custom search filter
  const searchFilter = (tire, search) => {
    const normalizedSelectedQuality = qualityFilter.toLowerCase();
    const normalizedTireQuality = String(tire.quality || tire.type || "")
      .trim()
      .toLowerCase();
    if (
      normalizedSelectedQuality !== "all" &&
      normalizedTireQuality !== normalizedSelectedQuality
    ) {
      return false;
    }
    if (excludeZeroQty && (Number(tire.quantity) || 0) === 0) return false;
    if (!search) return true;
    const term = search.toLowerCase();
    const normalizedTerm = term.replace(/[^a-z0-9]/g, "");
    const normalizedSize = (tire.size || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    return (
      (tire.brand || "").toLowerCase().includes(term) ||
      (tire.size || "").toLowerCase().includes(term) ||
      (normalizedTerm && normalizedSize.includes(normalizedTerm)) ||
      (tire.quality || "").toLowerCase().includes(term) ||
      String(tire.buyPrice || "")
        .toLowerCase()
        .includes(term) ||
      String(tire.price || "")
        .toLowerCase()
        .includes(term) ||
      String(tire.quantity || "")
        .toLowerCase()
        .includes(term) ||
      (tire.vendor || "").toLowerCase().includes(term) ||
      (tire.location?.name || "").toLowerCase().includes(term) ||
      String(tire.invoiceNo || "").toLowerCase().includes(term) ||
      String(tire.po || "").toLowerCase().includes(term)
    );
  };

  const quickFilters = [
    {
      key: "all",
      label: "All",
      active: qualityFilter === "All",
      onClick: () => setQualityFilter("All"),
    },
    {
      key: "new",
      label: "New",
      active: qualityFilter === "New",
      onClick: () => setQualityFilter("New"),
    },
    {
      key: "used",
      label: "Used",
      active: qualityFilter === "Used",
      onClick: () => setQualityFilter("Used"),
    },
    {
      key: "exclude-zero",
      label: "Exclude 0",
      active: excludeZeroQty,
      onClick: () => setExcludeZeroQty((prev) => !prev),
    },
  ];

  // Custom sort function
  const sortFunction = (a, b) =>
    (a.brand || "").localeCompare(b.brand || "", undefined, {
      sensitivity: "base",
    });

  const onAfterFetch = useCallback((data = []) => {
    let newQty = 0;
    let usedQty = 0;
    data.forEach((item) => {
      const quality = String(item.quality || item.condition || item.type || "")
        .trim()
        .toLowerCase();
      const quantity = Number(item.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) return;
      if (quality === "new") newQty += quantity;
      if (quality === "used") usedQty += quantity;
    });

    setTotalNewQty(newQty);
    setTotalUsedQty(usedQty);
  }, []);

  const stockSummary = (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }} aria-label="Tires in stock">
      <Chip label={`New tires in stock: ${totalNewQty.toLocaleString()}`}
        sx={{ backgroundColor: "#e8eef8", color: "#36589A", fontWeight: 600 }} />
      <Chip label={`Used tires in stock: ${totalUsedQty.toLocaleString()}`}
        sx={{ backgroundColor: "#ecfdf3", color: "#166534", fontWeight: 600 }} />
    </Box>
  );

  // Get item name for dialogs
  const getItemName = (tire) => (tire ? `${tire.brand} ${tire.size}` : "");

  const onBeforeSave = (values) =>
    buildTirePayload(values, Helpers.authParentId);

  // Handle receive stock
  const openReceiveModal = (item) => {
    setReceiveItem(item);
    setReceiveForm({
      ...receiveStockInit,
      vendor: item.vendor || "",
      invoiceNo: item.invoiceNo || "",
      po: item.po || "",
    });
    setOpenReceiveDialog(true);
  };

  const handleReceiveStock = (values) => {
    if (receiveSubmitLock.current || receiveLoading) return;
    if (!receiveItem?._id) {
      Helpers.toast("error", "No tire selected");
      return;
    }

    receiveSubmitLock.current = true;
    setReceiveLoading(true);

    const payload = {
      _id: receiveItem._id,
      quantity: parseInt(values.quantity, 10) || 0,
      vendor: values.vendor || "",
      invoiceNo: values.invoiceNo || "",
      po: values.po || "",
      date: values.date || new Date().toISOString().slice(0, 10),
      note: values.note || "",
    };

    // Using exact same URL pattern as original working code
    axios
      .post(`${Helpers.baseUrl}/tires/receive-stock`, payload, Helpers.headers)
      .then((response) => {
        // Update receiveItem with the fresh data from backend
        if (response.data && response.data.tire) {
          setReceiveItem(response.data.tire);
        } else if (response.data) {
          setReceiveItem(response.data);
        }

        Helpers.toast("success", "Stock received successfully");

        // Trigger refresh in ModuleLayout
        setRefreshTrigger((prev) => prev + 1);
        setOpenReceiveDialog(false);
        setReceiveItem(null);
        setReceiveForm(receiveStockInit);
      })
      .catch((error) => {
        console.error("Receive stock error:", error);
        Helpers.toast("error", Helpers.error_response(error));
      })
      .finally(() => {
        setReceiveLoading(false);
        receiveSubmitLock.current = false;
      });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  // Data Grid columns configuration
  const columns = [
    {
      field: "brand",
      headerName: "Brand",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "size",
      headerName: "Size",
      width: 120,
    },
    {
      field: "quantity",
      headerName: "Qty",
      width: 70,
    },
    {
      field: "location",
      headerName: "Location",
      minWidth: 140,
      valueGetter: (value) => value?.name || "",
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "buyPrice",
      headerName: "Buy Price",
      width: 140,
      renderCell: (params) => <MaskedBuyPriceCell value={params.row.buyPrice} />,
    },
    {
      field: "price",
      headerName: "Sell Price",
      width: 110,
      renderCell: (params) => formatMoney(params.row.price),
    },
    {
      field: "quality",
      headerName: "Type",
      width: 120,
    },
    {
      field: "invoiceNo",
      headerName: "Invoice No.",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => params.row.invoiceNo || "-",
    },
    {
      field: "po",
      headerName: "PO",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => params.row.po || "-",
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => params.row.vendor || "-",
    },
  ];

  // Custom actions renderer with receive stock button
  const customActionsRenderer = (
    params,
    canUpdate,
    canDelete,
    handleEdit,
    handleDelete,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: 1,
      }}
    >
      {canUpdate && (
        <IconButton
          onClick={() => openReceiveModal(params.row)}
          color="info"
          size="small"
          title="Receive Stock"
        >
          <AddCircleIcon fontSize="small" />
        </IconButton>
      )}
      {canUpdate && (
        <IconButton
          onClick={() => handleEdit(params.row)}
          color="success"
          size="small"
          title="Edit Tire"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          onClick={() => handleDelete(params.row)}
          color="error"
          size="small"
          title="Delete Tire"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      <ModuleLayout
        moduleName="Tires"
        moduleKey="tires"
        icon={require("../images/icons/tire.png")}
        initialFormValues={tireInit}
        formFields={tireFields}
        validationSchema={tireValidationSchema}
        columns={columns}
        searchFilter={searchFilter}
        quickFilters={quickFilters}
        sortFunction={sortFunction}
        getItemName={getItemName}
        onBeforeSave={onBeforeSave}
        mapItemToFormValues={(item) => prepareTireFormValues(item, Helpers.authParentId)}
        onAfterFetch={onAfterFetch}
        headerSummary={stockSummary}
        customActionsRenderer={customActionsRenderer}
        deleteWarning="This action cannot be undone. All associated records will remain but will no longer be linked to this tire."
        key={refreshTrigger}
      />
      <ReusableFormDialog
        open={openReceiveDialog}
        onClose={() => {
          setOpenReceiveDialog(false);
          setReceiveForm(receiveStockInit);
          setReceiveItem(null);
        }}
        title={`Receive Stock: ${receiveItem ? `${receiveItem.brand} ${receiveItem.size} (${receiveItem.quality})` : ""}`}
        initialValues={receiveForm}
        validationSchema={receiveStockValidationSchema}
        fields={receiveStockFields}
        onSubmit={handleReceiveStock}
        loading={receiveLoading}
        maxWidth="md"
      >
        {receiveItem &&
          Array.isArray(receiveItem.purchaseHistory) &&
          receiveItem.purchaseHistory.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  Purchase History
                </Typography>
                <TableContainer
                  sx={{
                    maxHeight: 250,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Date
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Qty
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Vendor
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Invoice
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          PO
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Note
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...receiveItem.purchaseHistory]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 10)
                        .map((entry, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                          >
                            <TableCell>{formatDate(entry.date)}</TableCell>
                            <TableCell>{entry.quantity}</TableCell>
                            <TableCell>{entry.vendor || "-"}</TableCell>
                            <TableCell>{entry.invoiceNo || "-"}</TableCell>
                            <TableCell>{entry.po || "-"}</TableCell>
                            <TableCell>{entry.note || "-"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
      </ReusableFormDialog>
    </>
  );
}

export default Tires;
